import { useAutoneTranslation } from '@autone/translations';
import { format } from 'date-fns';
import { type ComponentProps, forwardRef, useCallback, useMemo } from 'react';

import {
  cn,
  TailwindInput,
  TailwindNumberInput,
  TailwindSelect,
  TailwindSelectContent,
  TailwindSelectGroup,
  TailwindSelectItem,
  TailwindSelectTrigger,
  TailwindSelectValue,
} from '../../../../../tailwind';
import {
  type SingleDatePickerProps,
  TailwindDatePicker,
} from '../../../../../tailwind/DatePicker';
import { type InputProps } from '../../../../../tailwind/inputs/Input';
import { type NumberInputProps } from '../../../../../tailwind/inputs/NumberInput';

/**
 * A default text input for string values.
 */
const DefaultStringInput = forwardRef<HTMLInputElement, InputProps>(
  ({ ...props }, ref) => {
    const { common } = useAutoneTranslation();

    return (
      <TailwindInput
        type="text"
        variant={'default'}
        placeholder={common('advanced-filters.placeholders.enter-a-value')}
        {...props}
        ref={ref}
      />
    );
  },
);

DefaultStringInput.displayName = 'AdvancedFilterDefaultStringInput';

/**
 * A default number input for numeric values.
 */
const DefaultNumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ ...props }, ref) => {
    const { common } = useAutoneTranslation();

    return (
      <TailwindNumberInput
        variant={'default'}
        showStepper={false}
        placeholder={common('advanced-filters.placeholders.enter-a-value')}
        {...props}
        ref={ref}
      />
    );
  },
);

interface DefaultSelectInputProps<T extends string = string>
  extends Omit<
    ComponentProps<typeof TailwindSelect>,
    'value' | 'onValueChange'
  > {
  value?: T;
  onValueChange: (value: T) => void;
  options: { id: T; label: string }[];
}

/**
 * A default input for single-select values.
 */
function DefaultSingleSelectInput<T extends string = string>({
  value,
  onValueChange,
  options,
  ...props
}: DefaultSelectInputProps<T>) {
  const { common } = useAutoneTranslation();

  return (
    <TailwindSelect value={value} onValueChange={onValueChange} {...props}>
      <TailwindSelectTrigger>
        <TailwindSelectValue
          placeholder={common('advanced-filters.placeholders.enter-a-value')}
        />
      </TailwindSelectTrigger>
      <TailwindSelectContent data-testid="advanced-filters-column-select-content">
        <TailwindSelectGroup>
          {options.map(({ id, label }) => (
            <TailwindSelectItem key={id} value={id}>
              {label}
            </TailwindSelectItem>
          ))}
        </TailwindSelectGroup>
      </TailwindSelectContent>
    </TailwindSelect>
  );
}

DefaultNumberInput.displayName = 'AdvancedFilterDefaultNumberInput';

interface DefaultSingleDatePickerProps
  extends Omit<SingleDatePickerProps, 'date' | 'onChange' | 'placeholderText'> {
  /**
   * A date string in 'yyyy-MM-dd' format.
   */
  value?: string;
  /**
   * Callback when the date value changes. The value is a date string in 'yyyy-MM-dd' format.
   */
  onValueChange: (value?: string) => void;
  placeholderText?: string;
}

/**
 * A default single date picker input for single date values. Uses date strings in 'yyyy-MM-dd'
 * format.
 */
const DefaultSingleDatePicker = forwardRef<
  HTMLButtonElement,
  DefaultSingleDatePickerProps
>(({ value, onValueChange, inputClassName, ...props }, ref) => {
  const { common } = useAutoneTranslation();

  const valAsDate = useMemo(
    () => (value ? new Date(value) : undefined),
    [value],
  );

  const handleChange = useCallback(
    (date: Date) => {
      onValueChange(format(date, 'yyyy-MM-dd'));
    },
    [onValueChange],
  );

  return (
    <TailwindDatePicker
      date={valAsDate}
      onChange={handleChange}
      inputClassName={cn('w-full h-12 rounded-md', inputClassName)}
      placeholderText={common('pick-a-date')}
      {...props}
      ref={ref}
    />
  );
});

DefaultSingleDatePicker.displayName = 'AdvancedFilterDefaultSingleDatePicker';

export const AdvancedFiltersDefaultInputs = {
  String: DefaultStringInput,
  Number: DefaultNumberInput,
  SingleDate: DefaultSingleDatePicker,
  SingleSelect: DefaultSingleSelectInput,
};
